import React, { useEffect, useState } from "react"
import dayjs from "dayjs"
import { makeStyles } from "@mui/styles"
import { Stack, Typography } from "@mui/material"
import { COLORS, roundNumber } from "../../utils"
import ProductionStepExecutionCardContainer from "./productionStepExecutionInProgressCases/ProductionStepExecutionCardContainer"

const useStyles = makeStyles({
    table: {
        width: "100%",
        borderCollapse: "collapse",
    },
    headerRow: {
        borderBottom: `1px solid ${COLORS.BLACK_200}`,
    },
    headerCell: {
        padding: "8px 12px",
        color: COLORS.BLACK_500,
        fontSize: "14px",
        fontWeight: 400,
        textAlign: "center",
        "&:first-child": {
            textAlign: "left",
        },
    },
    row: {
        borderBottom: `1px solid ${COLORS.BACKGROUND_LIGHT_GREY}`,
        "&:last-child": {
            borderBottom: "none",
        },
    },
    cell: {
        padding: "16px 12px",
        textAlign: "center",
        "&:first-child": {
            textAlign: "left",
        },
    },
    dateLabel: {
        color: COLORS.BLACK800,
        fontSize: "16px",
        fontWeight: 500,
        lineHeight: "22px",
    },
    dateSubLabel: {
        color: COLORS.BLACK_500,
        fontSize: "14px",
        fontWeight: 400,
    },
    theoreticalWeight: {
        color: COLORS.BLACK800,
        fontSize: "20px",
        fontWeight: 400,
    },
    inputWrapper: {
        display: "flex",
        justifyContent: "center",
    },
    input: {
        fontSize: "20px",
        fontWeight: 400,
        textAlign: "center",
        color: COLORS.BLACK800,
        padding: "4px 0",
        border: "none",
        borderBottom: `1px solid ${COLORS.BLACK_500}`,
        outline: "none",
        width: "100px",
        "&:focus": { borderBottom: `1px solid ${COLORS.PRIMARY_COLOR}` },
        "&::placeholder": { color: COLORS.BLACK_300 },
    },
    unit: {
        color: COLORS.BLACK_500,
        fontSize: "14px",
        marginLeft: "4px",
        alignSelf: "flex-end",
        paddingBottom: "6px",
    },
    totalizerRow: {
        borderTop: `1px solid ${COLORS.BLACK_200}`,
        paddingTop: "16px",
        marginTop: "8px",
    },
    totalizerLabel: {
        color: COLORS.BLACK_700,
        fontSize: "16px",
        fontWeight: 400,
    },
    totalizerValue: {
        fontSize: "18px",
        fontWeight: 600,
    },
})

export const computePackagingDatesData = (packagingExecutions, theoreticalNetWeight, productionDate) => {
    const dateMap = new Map()
    let totalPortions = 0

    for (const pe of packagingExecutions) {
        const date = pe.packagingDate
        if (!date) continue
        const portions = (pe.packagings || []).reduce((sum, p) => sum + (p.theoreticalNumber || 0), 0)
        totalPortions += portions
        if (!dateMap.has(date)) {
            dateMap.set(date, 0)
        }
        dateMap.set(date, dateMap.get(date) + portions)
    }

    // Fallback when theoreticalNumber not yet set: use expectedProduction from productionItems
    if (totalPortions === 0) {
        for (const pe of packagingExecutions) {
            const date = pe.packagingDate
            if (!date) continue
            const portions = (pe.productionItems || [])
                .reduce((sum, pi) => sum + (pi.expectedProduction || 0) / (pi.packagingNumber || 1), 0)
            totalPortions += portions
            dateMap.set(date, (dateMap.get(date) || 0) + portions)
        }
    }

    if (dateMap.size <= 1) return []

    const productionDay = dayjs(productionDate)
    return Array.from(dateMap.entries())
        .sort(([a], [b]) => dayjs(a).valueOf() - dayjs(b).valueOf())
        .map(([date, portions]) => {
            const d = dayjs(date)
            const diffDays = d.startOf("day").diff(productionDay.startOf("day"), "day")
            const relativeLabel = diffDays === 0 ? "Aujourd'hui" : diffDays === 1 ? "Demain" : d.format("dddd")
            const dateLabel = d.format("D MMM")
            const theoreticalWeight = totalPortions > 0
                ? roundNumber((theoreticalNetWeight || 0) * (portions / totalPortions), 2)
                : 0
            return { date, relativeLabel, dateLabel, theoreticalWeight }
        })
}

const PackagingDateWeightDistribution = ({
    packagingExecutions,
    productionDate,
    theoreticalNetWeight,
    netWeight,
    packagingDateWeights,
    onChange,
    error,
    onDistributionBalancedChange,
}) => {
    const classes = useStyles()

    const [totalDistributed, setTotalDistributed] = useState(0)
    const [isBalanced, setIsBalanced] = useState(false)
    const [rows, setRows] = useState([])

    useEffect(() => {
        setRows(computePackagingDatesData(packagingExecutions || [], theoreticalNetWeight, productionDate))
    }, [packagingExecutions, theoreticalNetWeight, productionDate])

    useEffect(() => {
        const total = rows.reduce((sum, row) => {
            return sum + Number(packagingDateWeights[row.date] || 0)
        }, 0)
        setTotalDistributed(total)

        const currentIsBalanced = netWeight != null && Math.abs(roundNumber(total, 2) - Number(netWeight)) <= 0.01
        setIsBalanced(currentIsBalanced)
        if (onDistributionBalancedChange) {
            onDistributionBalancedChange(currentIsBalanced)
        }
    }, [rows, packagingDateWeights, onDistributionBalancedChange, netWeight])


    if (rows.length === 0) return null

    const totalizerColor = netWeight == null
        ? COLORS.BLACK_500
        : isBalanced
            ? COLORS.GREEN_800
            : COLORS.RED_ERROR

    return (
        <ProductionStepExecutionCardContainer
            title="Répartition par jour de barquettage"
            icon="/img/recipes/balance.svg"
            error={error}
        >
            <table className={classes.table}>
                <thead>
                    <tr className={classes.headerRow}>
                        <th className={classes.headerCell}>Date</th>
                        <th className={classes.headerCell}>Poids prévu</th>
                        <th className={classes.headerCell}>Poids réparti</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(({ date, relativeLabel, dateLabel, theoreticalWeight }) => (
                        <tr key={date} className={classes.row}>
                            <td className={classes.cell}>
                                <div className={classes.dateLabel}>{relativeLabel}</div>
                                <div className={classes.dateSubLabel}>({dateLabel})</div>
                            </td>
                            <td className={classes.cell}>
                                <span className={classes.theoreticalWeight}>{theoreticalWeight}</span>
                                <span style={{ color: COLORS.BLACK_500, fontSize: "14px", marginLeft: "4px" }}>kg</span>
                            </td>
                            <td className={classes.cell}>
                                <div className={classes.inputWrapper}>
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        step="any"
                                        className={classes.input}
                                        value={packagingDateWeights[date] ?? ""}
                                        onChange={(e) => onChange(date, e.target.value === "" ? null : Number(e.target.value))}
                                        placeholder="0"
                                        onWheel={(e) => e.target?.blur()}
                                    />
                                    <span className={classes.unit}>kg</span>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <Stack direction="row" justifyContent="flex-end" alignItems="center" className={classes.totalizerRow} gap={1}>
                <Typography className={classes.totalizerLabel}>Poids total attribué :</Typography>
                <Typography className={classes.totalizerValue} sx={{ color: totalizerColor }}>
                    {roundNumber(totalDistributed, 2)} / {netWeight ?? "—"} kg
                </Typography>
                {isBalanced && (
                    <Typography sx={{ color: COLORS.GREEN_800, fontSize: "18px" }}>✓</Typography>
                )}
            </Stack>
        </ProductionStepExecutionCardContainer>
    )
}

export default PackagingDateWeightDistribution
